import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, SignIn, SignUp } from '@clerk/clerk-react';
import {
  Loader, CheckCircle2, AlertCircle, Mail, Building2, ArrowRight, LogIn, UserPlus
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api`;

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { isLoaded, isSignedIn, getToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);

  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [acceptError, setAcceptError] = useState(null);

  const [authMode, setAuthMode] = useState(null); // 'signin' | 'signup'

  const fetchInvite = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/partner/invites/${token}`);
      const data = await response.json();
      if (!data.success) {
        setError(data.error || 'Invitation not found');
        return;
      }
      setInvite(data.invite);
    } catch (err) {
      setError(err.message || 'Could not load invitation');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchInvite(); }, [fetchInvite]);

  // Auto-accept on first load if already signed in (also works after Clerk redirects back)
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !invite || accepted || accepting) return;
    if (invite.expired || invite.revoked) return;
    handleAccept();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, invite]);

  const handleAccept = async () => {
    setAccepting(true);
    setAcceptError(null);
    try {
      const authToken = await getToken();
      const response = await fetch(`${API_URL}/partner/invites/${token}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!data.success) {
        setAcceptError(data.error || 'Could not accept invitation');
        return;
      }
      setAccepted(true);
      setTimeout(() => navigate('/partner'), 1500);
    } catch (err) {
      setAcceptError(err.message);
    } finally {
      setAccepting(false);
    }
  };

  const pageStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: theme.mode === 'light'
      ? 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)'
      : 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
  };

  const cardStyle = {
    width: '100%',
    maxWidth: 520,
    background: theme.colors.bg.secondary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: 16,
    padding: 32,
    boxShadow: theme.mode === 'light'
      ? '0 24px 48px rgba(0,0,0,0.12)'
      : '0 24px 48px rgba(0,0,0,0.5)'
  };

  if (loading || !isLoaded) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, color: theme.colors.text.tertiary }}>
            <Loader size={20} className="animate-spin" />
            <span>Loading invitation…</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <ErrorState
            theme={theme}
            title="Invitation not found"
            description="The link may have expired or been mistyped. Ask the sender for a new invitation."
          />
        </div>
      </div>
    );
  }

  if (invite?.expired) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <ErrorState
            theme={theme}
            title="This invitation has expired"
            description={`The invitation for ${invite.organizationName} is no longer valid. Ask the owner to send a new one.`}
          />
        </div>
      </div>
    );
  }

  if (invite?.revoked) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <ErrorState
            theme={theme}
            title="Invitation revoked"
            description={`The owner of ${invite.organizationName} has revoked this invitation.`}
          />
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center'
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(16, 185, 129, 0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <CheckCircle2 size={30} color="#10b981" />
            </div>
            <h1 style={{ color: theme.colors.text.primary, fontSize: 22, fontWeight: 700, margin: 0 }}>
              Welcome to {invite.organizationName}!
            </h1>
            <p style={{ color: theme.colors.text.secondary, fontSize: 14, margin: 0 }}>
              Taking you to the Partner Dashboard…
            </p>
            <Loader size={16} className="animate-spin" color={theme.colors.text.muted} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(139, 92, 246, 0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <Building2 size={28} color="#8b5cf6" />
          </div>
          <h1 style={{
            color: theme.colors.text.primary, fontSize: 22, fontWeight: 700, margin: '0 0 8px'
          }}>
            You've been invited!
          </h1>
          <p style={{ color: theme.colors.text.secondary, fontSize: 14, margin: 0, lineHeight: 1.55 }}>
            <strong>{invite.inviterName || invite.inviterEmail || 'A Bidoryx partner'}</strong> has invited you
            to join the <strong>{invite.organizationName}</strong> team on Bidoryx.
          </p>
        </div>

        <div style={{
          padding: 14,
          background: theme.colors.bg.tertiary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 10,
          marginBottom: 20
        }}>
          <Row theme={theme} label="Organization" value={invite.organizationName} />
          <Row theme={theme} label="Role" value={(invite.role || 'member').toUpperCase()} />
          <Row theme={theme} label="Sent to" value={invite.email} icon={<Mail size={13} color={theme.colors.text.muted} />} />
        </div>

        {invite.message && (
          <div style={{
            padding: 14,
            background: 'rgba(139, 92, 246, 0.08)',
            border: '1px solid rgba(139, 92, 246, 0.25)',
            borderRadius: 10,
            marginBottom: 20
          }}>
            <div style={{
              color: theme.colors.text.tertiary, fontSize: 11, fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6
            }}>
              Personal message
            </div>
            <div style={{
              color: theme.colors.text.primary, fontSize: 14, lineHeight: 1.55,
              whiteSpace: 'pre-line'
            }}>
              {invite.message}
            </div>
          </div>
        )}

        {acceptError && (
          <div style={{
            padding: '10px 12px',
            background: 'rgba(239, 68, 68, 0.10)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 8,
            color: '#ef4444',
            fontSize: 13,
            marginBottom: 16,
            display: 'flex', alignItems: 'flex-start', gap: 8
          }}>
            <AlertCircle size={15} style={{ marginTop: 1, flexShrink: 0 }} />
            <span>{acceptError}</span>
          </div>
        )}

        {!isSignedIn ? (
          <>
            <div style={{
              padding: 14,
              background: 'rgba(59, 130, 246, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.25)',
              borderRadius: 10,
              marginBottom: 20,
              fontSize: 13,
              color: theme.colors.text.secondary,
              lineHeight: 1.55
            }}>
              To accept this invitation, sign in or create a Bidoryx account
              with <strong>{invite.email}</strong>.
            </div>

            {authMode === 'signin' ? (
              <SignIn
                routing="hash"
                signUpUrl={`/invite/${token}#/sign-up`}
                fallbackRedirectUrl={`/invite/${token}`}
                forceRedirectUrl={`/invite/${token}`}
                initialValues={{ emailAddress: invite.email }}
              />
            ) : authMode === 'signup' ? (
              <SignUp
                routing="hash"
                signInUrl={`/invite/${token}#/sign-in`}
                fallbackRedirectUrl={`/invite/${token}`}
                forceRedirectUrl={`/invite/${token}`}
                initialValues={{ emailAddress: invite.email }}
              />
            ) : (
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setAuthMode('signup')}
                  style={{
                    flex: 1,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    background: '#8b5cf6', color: '#fff',
                    border: '1px solid #8b5cf6',
                    borderRadius: 8, padding: '12px 14px',
                    fontSize: 14, fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  <UserPlus size={15} /> Create account
                </button>
                <button
                  onClick={() => setAuthMode('signin')}
                  style={{
                    flex: 1,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    background: 'transparent',
                    color: theme.colors.text.primary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: 8, padding: '12px 14px',
                    fontSize: 14, fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  <LogIn size={15} /> I already have an account
                </button>
              </div>
            )}
          </>
        ) : (
          <button
            onClick={handleAccept}
            disabled={accepting}
            style={{
              width: '100%',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              background: '#8b5cf6', color: '#fff',
              border: '1px solid #8b5cf6',
              borderRadius: 8, padding: '12px 14px',
              fontSize: 14, fontWeight: 600,
              cursor: accepting ? 'not-allowed' : 'pointer',
              opacity: accepting ? 0.7 : 1,
              boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
            }}
          >
            {accepting ? <Loader size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
            {accepting ? 'Accepting…' : 'Accept invitation'}
            {!accepting && <ArrowRight size={15} />}
          </button>
        )}

        <div style={{
          marginTop: 18, textAlign: 'center',
          color: theme.colors.text.muted, fontSize: 12
        }}>
          <Link to="/" style={{ color: theme.colors.text.muted, textDecoration: 'none' }}>
            ← Back to Bidoryx
          </Link>
        </div>
      </div>
    </div>
  );
}

function Row({ theme, label, value, icon }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '6px 0',
      borderBottom: `1px dashed ${theme.colors.border.secondary}`,
      gap: 12
    }}>
      <span style={{
        color: theme.colors.text.tertiary, fontSize: 12,
        fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4
      }}>
        {label}
      </span>
      <span style={{
        color: theme.colors.text.primary, fontSize: 13, fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: 6,
        textAlign: 'right',
        wordBreak: 'break-all'
      }}>
        {icon} {value}
      </span>
    </div>
  );
}

function ErrorState({ theme, title, description }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 12, textAlign: 'center'
    }}>
      <div style={{
        width: 54, height: 54, borderRadius: '50%',
        background: 'rgba(239, 68, 68, 0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <AlertCircle size={28} color="#ef4444" />
      </div>
      <h2 style={{ color: theme.colors.text.primary, fontSize: 19, fontWeight: 700, margin: 0 }}>{title}</h2>
      <p style={{ color: theme.colors.text.secondary, fontSize: 14, margin: 0, lineHeight: 1.55 }}>{description}</p>
      <Link
        to="/"
        style={{
          marginTop: 6,
          color: '#8b5cf6', fontSize: 13, fontWeight: 600, textDecoration: 'none'
        }}
      >
        Go to Bidoryx →
      </Link>
    </div>
  );
}
